import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { FakeLink } from '@/core/FakeLink';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { SEEQ_DATASOURCE } from '@/main/app.constants';
import { useSearchDatasources } from '@/search/search.store';
import { isAdmin } from '@/services/authorization.service';
import { successToast } from '@/utilities/toast.utilities';
import { setUserDatasources, setUserPreferencesDisplay } from '@/workbench/workbench.actions';
import { Button } from '@seeqdev/qomponents';
import type { Datasource } from '@/search/search.types';

/**
 * Tab to hide/show datasources based on user preferences; user can choose which
 * datasources show in their search filters.
 */
const getCheckboxBGColor = (isChecked: boolean) => {
  return isChecked ? 'sq-bg-included' : 'sq-bg-excluded';
};
export const SaveDatasourcePreferencesTab = () => {
  const { stateParams } = useFlux(sqWorkbenchStore);
  const { t } = useTranslation();

  const datasources = useSearchDatasources();
  const { userDatasources } = useFlux(sqWorkbenchStore);
  const { includeNewDatasources } = useFlux(sqWorkbenchStore);

  const allPossibleDatasources = _.chain(datasources)
    .reject(
      (datasource) =>
        datasource.datasourceClass === SEEQ_DATASOURCE.datasourceClass || datasource.scopedTo !== undefined,
    )
    .sortBy('name')
    .value();

  const [isIncludeNewChecked, setIsIncludeNewChecked] = useState(includeNewDatasources);
  const [selectedDatasources, setSelectedDatasources] = useState<Datasource[]>(
    allPossibleDatasources.filter((datasource) =>
      includeNewDatasources ? !userDatasources.includes(datasource.id) : userDatasources.includes(datasource.id),
    ),
  );

  const [allCheckbox, setAllCheckbox] = useState(selectedDatasources.length > 0);
  const handleSelectAllCheckbox = () => {
    if (!allCheckbox) {
      setSelectedDatasources(allPossibleDatasources.map((datasource) => datasource));
      setAllCheckbox(true);
    } else {
      setSelectedDatasources([]);
    }
  };

  /**
   * Automatically unchecks the "All" checkbox if selectedDatasources reaches 0 in length
   */
  useEffect(() => {
    if (_.isEmpty(selectedDatasources)) {
      setAllCheckbox(false);
    }
  }, [selectedDatasources]);

  const handleSelectDatasource = (datasource: Datasource) => {
    if (!selectedDatasources.some((sd) => sd.id === datasource.id)) {
      setSelectedDatasources((prev) => [...prev, datasource]);
      setAllCheckbox(true);
    } else {
      setSelectedDatasources((prev) => prev.filter((existing) => existing.id !== datasource.id));
    }
  };

  const isChecked = (datasource: Datasource) => {
    return selectedDatasources.some((sd) => sd.id === datasource.id);
  };

  const onExcludeModeChange = (exclude: boolean) => {
    setIsIncludeNewChecked(exclude);
  };

  const RadioGroup = (
    <div className="flexAlignLeft tw:mb-sq-10 tw:ml-sq-5 tw:pt-1">
      <div className="tw:mt-sq-10 tw:mb-sq-5">
        <b className="tw:mt-sq-5">{t('USER.DATASOURCE_PREFERENCES_NEWLY_ADDED')}</b>
      </div>
      <div className="form-check form-check-inline flexColumnContainer">
        <Checkbox
          id="datasource-preference-include"
          type="radio"
          onChange={() => onExcludeModeChange(true)}
          isChecked={isIncludeNewChecked}
          label={t('USER.DATASOURCE_INCLUDED_MODE')}
          classes="form-check-input"
        />
      </div>
      <div className="form-check form-check-inline flexColumnContainer">
        <Checkbox
          id="datasource-preference-exclude"
          type="radio"
          onChange={() => onExcludeModeChange(false)}
          isChecked={!isIncludeNewChecked}
          label={t('USER.DATASOURCE_EXCLUDED_MODE')}
          classes="form-check-input"
        />
      </div>
    </div>
  );

  return (
    <div className="flexRowContainer tw:overflow-hidden" data-testid="saveDatasourcePreferencesTab">
      <div className="flexColumnContainer tw:justify-between tw:pb-sq-10">
        <b className="tw:mb-sq-5">{t('USER.DATASOURCE_PREFERENCES_TITLE')}</b>
        {isAdmin() && (
          <FakeLink
            testId="manage-datasources"
            onClick={() => {
              window.open('/administration', 'SeeqAdmin');
            }}>
            {t('DATASOURCES.MANAGEMENT')}
          </FakeLink>
        )}
      </div>
      <div className="tw:mb-sq-5">{t('USER.DATASOURCE_PREFERENCES_DETAILS')}</div>
      <div className="tw:pl-sq-3">
        <Checkbox
          id="checkbox-toggle-all-datasources"
          label={t('USER.DATASOURCE_PREFERENCES_ALL_DATASOURCES_CHECKBOX_LABEL', {
            count: selectedDatasources.length,
            total: allPossibleDatasources.length,
          })}
          classes="pl2 pb5 pt5"
          isChecked={allCheckbox || selectedDatasources.length > 0}
          onClick={() => handleSelectAllCheckbox()}
        />
      </div>
      <div className="overflowYScroll tw:mt-sq-5 tw:h-sq-200!">
        {allPossibleDatasources.map((datasource) => (
          <div key={datasource.id} className={`tw:pl-sq-3 ${getCheckboxBGColor(isChecked(datasource))}`}>
            <Checkbox
              id={`checkbox-${datasource.id}`}
              label={`${datasource.name}`}
              classes="l2 pb5 pt5"
              isChecked={isChecked(datasource)}
              onClick={() => handleSelectDatasource(datasource)}
            />
          </div>
        ))}
      </div>
      {RadioGroup}
      <div className="flexRowContainer tw:items-center tw:pt-5">
        <Button
          testId="saveDatasourcePreferencesButton"
          type="button"
          extraClassNames="buttonDisabledTooltip"
          size="sm"
          variant="theme"
          onClick={() => {
            const updatedDatasources = isIncludeNewChecked
              ? datasources
                  .filter((ds) => !selectedDatasources.includes(ds) && !ds.scopedTo)
                  .map((datasource) => datasource.id)
              : selectedDatasources.map((datasource) => datasource.id);
            setUserDatasources(updatedDatasources, isIncludeNewChecked);
            successToast({ messageKey: 'USER.DATASOURCE_PREFERENCES_SAVED_TOAST' });
            setUserPreferencesDisplay(false);
          }}
          tooltip={t('USER.DATASOURCE_SAVE_PREFERENCES_BUTTON_TOOLTIP')}
          label={t('USER.SAVE_DATASOURCE_CHANGES_BUTTON')}
        />
      </div>
    </div>
  );
};
